(() => {
  document.documentElement.classList.add('js');

  const menu = document.querySelector('[data-mobile-menu]');
  const openButton = document.querySelector('[data-menu-open]');
  const closeButton = document.querySelector('[data-menu-close]');
  let menuReturnFocus = null;

  const setMenu = (open) => {
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    menu.toggleAttribute('inert', !open);
    openButton?.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('no-scroll', open);

    if (open) {
      menuReturnFocus = document.activeElement;
      closeButton?.focus();
    } else if (menuReturnFocus instanceof HTMLElement) {
      menuReturnFocus.focus();
      menuReturnFocus = null;
    }
  };

  openButton?.addEventListener('click', () => setMenu(true));
  closeButton?.addEventListener('click', () => setMenu(false));
  menu?.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenu(false)));

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setMenu(false);

    if (event.key === 'Tab' && menu?.classList.contains('is-open')) {
      const focusable = Array.from(menu.querySelectorAll('a[href], button:not([disabled])')).filter((element) => element.offsetParent !== null);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });

  document.querySelectorAll('[data-product-card]').forEach((card) => {
    const image = card.querySelector('[data-card-image]');
    card.querySelectorAll('[data-image-side]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!image) return;
        if (button.dataset.imageSrcset) image.srcset = button.dataset.imageSrcset;
        else image.removeAttribute('srcset');
        if (button.dataset.imageSizes) image.sizes = button.dataset.imageSizes;
        else image.removeAttribute('sizes');
        image.src = button.dataset.imageUrl;
        image.alt = button.dataset.imageAlt || `${image.dataset.productName} ${button.dataset.imageSide}`;
        card.querySelectorAll('[data-image-side]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
      });
    });
  });

  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const mainImage = gallery.querySelector('[data-main-product-image]');
    gallery.querySelectorAll('[data-product-thumb]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!mainImage) return;
        if (button.dataset.imageSrcset) mainImage.srcset = button.dataset.imageSrcset;
        else mainImage.removeAttribute('srcset');
        if (button.dataset.imageSizes) mainImage.sizes = button.dataset.imageSizes;
        else mainImage.removeAttribute('sizes');
        mainImage.src = button.dataset.imageUrl;
        mainImage.alt = button.dataset.imageAlt || mainImage.alt;
        gallery.querySelectorAll('[data-product-thumb]').forEach((item) => {
          const selected = item === button;
          item.classList.toggle('selected', selected);
          item.setAttribute('aria-pressed', String(selected));
        });
      });
    });
  });

  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const variantSelect = form.querySelector('select[name="id"]');
    const variantInput = form.querySelector('input[name="id"]');
    const variantButtons = Array.from(form.querySelectorAll('[data-variant-option]'));
    const variantHelp = form.querySelector('[data-variant-help]');
    const pagePrice = form.closest('.product-page-info')?.querySelector('[data-product-price]');
    const mainButton = form.querySelector('[data-main-add-button]');
    const stickyButton = form.querySelector('[data-sticky-add-button]');
    const mobilePurchaseBar = stickyButton?.closest('[data-mobile-purchase-bar]');
    const stickyPrice = mobilePurchaseBar?.querySelector('[data-sticky-product-price]');
    const productAvailable = form.dataset.productAvailable !== 'false';
    const productMediaReady = form.dataset.productMediaReady !== 'false';
    const canPurchaseProduct = productAvailable && productMediaReady;
    const unavailableLabel = productMediaReady ? 'SOLD OUT' : 'PRODUCT MEDIA REQUIRED';

    const syncVariantUi = () => {
      const variantControl = variantSelect?.selectedOptions?.[0] || variantInput;
      if (!variantControl) return;

      const price = variantControl.dataset.price || '';
      const hasSelection = Boolean(variantControl.value);
      const available = canPurchaseProduct && variantControl.dataset.available !== 'false';

      variantButtons.forEach((button) => {
        const selected = hasSelection && button.dataset.variantId === variantControl.value;
        button.classList.toggle('selected', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      if (!hasSelection) {
        if (variantHelp) variantHelp.textContent = variantHelp.dataset.defaultCopy || 'Choose a size before adding this piece to your bag.';
        if (mainButton) {
          mainButton.disabled = true;
          mainButton.textContent = canPurchaseProduct ? 'CHOOSE A SIZE' : unavailableLabel;
        }
        if (stickyButton) {
          stickyButton.disabled = true;
          stickyButton.textContent = canPurchaseProduct ? 'CHOOSE A SIZE' : unavailableLabel;
        }
        return;
      }

      if (variantHelp) variantHelp.textContent = `Selected: ${variantControl.textContent.replace(/\s+—\s+SOLD OUT$/i, '').trim()}.`;

      if (pagePrice && price) pagePrice.textContent = price;
      if (stickyPrice && price) stickyPrice.textContent = price;
      if (mainButton) {
        mainButton.disabled = !available;
        mainButton.textContent = available ? `ADD TO CART — ${price}` : unavailableLabel;
      }
      if (stickyButton) {
        stickyButton.disabled = !available;
        stickyButton.textContent = available ? 'ADD TO CART' : unavailableLabel;
      }
    };

    variantSelect?.addEventListener('change', syncVariantUi);
    variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        if (!variantSelect || button.disabled) return;
        variantSelect.value = button.dataset.variantId || '';
        variantSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    stickyButton?.addEventListener('click', (event) => {
      event.preventDefault();
      if (!stickyButton.disabled) form.requestSubmit(mainButton);
    });

    if (mobilePurchaseBar && mainButton && 'IntersectionObserver' in window) {
      const purchaseBarObserver = new IntersectionObserver(([entry]) => {
        const mainActionIsVisible = entry.isIntersecting;
        mobilePurchaseBar.classList.toggle('is-hidden', mainActionIsVisible);
        mobilePurchaseBar.setAttribute('aria-hidden', String(mainActionIsVisible));
      }, { threshold: 0.35 });

      purchaseBarObserver.observe(mainButton);
    }

    syncVariantUi();
  });

  document.querySelectorAll('[data-complete-look]').forEach((section) => {
    const selects = Array.from(section.querySelectorAll('[data-look-variant]'));
    const currentSelect = section.querySelector('[data-look-role="current"]');
    const addButton = section.querySelector('[data-complete-look-add]');
    const total = section.querySelector('[data-look-total]');
    const status = section.querySelector('[data-complete-look-status]');
    const currency = section.dataset.currency || 'USD';
    const root = window.Shopify?.routes?.root || '/';
    const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency
    });

    let addInFlight = false;
    let cartReviewRequired = false;

    const selectedOptions = () => selects.map((select) => select.selectedOptions[0]);

    const selectedItems = () => selects.map((select) => {
      const option = select.selectedOptions[0];
      const role = select.dataset.lookRole || 'current';
      const isPairing = role === 'pairing';
      const priceCents = Number(option?.dataset.priceCents);
      const hasSelectedVariant = Boolean(select.value);

      return {
        role,
        product_id: isPairing
          ? section.dataset.pairingProductId
          : section.dataset.currentProductId,
        product_handle: isPairing
          ? section.dataset.pairingProductHandle
          : section.dataset.currentProductHandle,
        variant_id: hasSelectedVariant ? String(select.value) : null,
        price: hasSelectedVariant && Number.isFinite(priceCents)
          ? priceCents / 100
          : null,
        quantity: 1
      };
    });

    const hasCompleteSelection = () => selects.length === 2 && selects.every((select) => {
      const option = select.selectedOptions[0];
      return Boolean(select.value) && Boolean(option) && !option.disabled && option.dataset.available !== 'false';
    });

    const buildEventData = (items = selectedItems(), extra = {}) => {
      const hasValue = items.length === 2 && items.every((item) => (
        Boolean(item.variant_id) && Number.isFinite(item.price)
      ));

      const eventData = {
        schema_version: 1,
        placement: 'product_page',
        pairing_kind: section.dataset.pairingKind || 'styling_suggestion',
        currency,
        item_count: items.length,
        items,
        ...extra
      };

      if (hasValue) {
        eventData.value = items.reduce((sum, item) => sum + item.price, 0);
      }

      return eventData;
    };

    const publishCompleteLook = (eventName, eventData) => {
      if (typeof window.Shopify?.analytics?.publish !== 'function') return;

      try {
        const publishResult = window.Shopify.analytics.publish(eventName, eventData);
        if (publishResult && typeof publishResult.catch === 'function') {
          publishResult.catch(() => {});
        }
      } catch (error) {
        // Measurement must never interrupt the shopping experience.
      }
    };

    const syncCompleteLook = () => {
      if (addInFlight) return;
      if (cartReviewRequired) {
        if (addButton) {
          addButton.disabled = false;
          addButton.textContent = 'REVIEW BAG';
        }
        if (status) status.textContent = "We couldn't confirm the full look. Your bag may have changed. Review your bag before trying again.";
        return;
      }

      const ready = hasCompleteSelection();
      const priceCents = selectedOptions().reduce((sum, option) => sum + Number(option?.dataset.priceCents || 0), 0);

      if (total) total.textContent = ready ? formatter.format(priceCents / 100) : 'CHOOSE BOTH SIZES';
      if (addButton) {
        addButton.disabled = !ready;
        addButton.textContent = ready ? `ADD BOTH — ${formatter.format(priceCents / 100)}` : 'CHOOSE BOTH SIZES';
      }
      if (status) {
        status.textContent = ready
          ? 'Both sizes selected. Both pieces will be added at their regular prices.'
          : status.dataset.defaultCopy || 'Select one available size for each piece.';
      }
    };

    selects.forEach((select) => {
      select.addEventListener('change', () => {
        syncCompleteLook();
        publishCompleteLook(
          'hotheads_gallery:complete_look_interacted',
          buildEventData(selectedItems(), {
            interaction_type: 'variant_selected',
            interaction_role: select.dataset.lookRole || 'unknown'
          })
        );
      });
    });

    const mainVariantSelect = document.querySelector('[data-product-form] [data-variant-select]');
    if (mainVariantSelect && currentSelect) {
      const syncCurrentSelection = () => {
        if (addInFlight || cartReviewRequired) return;
        if (!mainVariantSelect.value) return;
        const matchingOption = Array.from(currentSelect.options).find((option) => option.value === mainVariantSelect.value && !option.disabled);
        if (!matchingOption) return;
        currentSelect.value = mainVariantSelect.value;
        syncCompleteLook();
      };
      mainVariantSelect.addEventListener('change', syncCurrentSelection);
      syncCurrentSelection();
    }

    section.querySelectorAll('[data-complete-look-link]').forEach((link) => {
      link.addEventListener('click', () => {
        publishCompleteLook(
          'hotheads_gallery:complete_look_interacted',
          buildEventData(selectedItems(), {
            interaction_type: 'product_link_clicked',
            interaction_role: link.dataset.lookLinkRole || 'unknown',
            target_product_id: link.dataset.targetProductId || null,
            target_product_handle: link.dataset.targetProductHandle || null
          })
        );
      });
    });

    let viewedPublished = false;
    const publishViewed = () => {
      if (viewedPublished) return;
      viewedPublished = true;
      publishCompleteLook(
        'hotheads_gallery:complete_look_viewed',
        buildEventData()
      );
    };

    if ('IntersectionObserver' in window) {
      const viewTarget = section.querySelector('.complete-look-heading') || section;
      const viewObserver = new IntersectionObserver(([entry]) => {
        if (!entry?.isIntersecting) return;
        publishViewed();
        viewObserver.disconnect();
      }, { threshold: 0.5 });

      viewObserver.observe(viewTarget);
    } else {
      publishViewed();
    }

    addButton?.addEventListener('click', async () => {
      if (addInFlight) return;
      if (cartReviewRequired) {
        window.location.assign(`${root}cart`);
        return;
      }
      if (!hasCompleteSelection()) return;

      addInFlight = true;
      selects.forEach((select) => { select.disabled = true; });
      section.setAttribute('aria-busy', 'true');
      const requestedItems = selectedItems();
      const requestEventData = buildEventData(requestedItems);
      const requestedVariantIds = requestedItems.map((item) => item.variant_id);
      let failureReason = 'request_error';
      let httpStatus = null;

      publishCompleteLook(
        'hotheads_gallery:complete_look_add_attempted',
        requestEventData
      );

      addButton.disabled = true;
      addButton.textContent = 'ADDING THE LOOK…';
      if (status) status.textContent = 'Adding both selected pieces to your bag.';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: requestedItems.map((item) => ({
              id: item.variant_id,
              quantity: item.quantity
            }))
          })
        });

        httpStatus = response.status;

        if (!response.ok) {
          failureReason = 'http_error';
          throw new Error('The selected look could not be added.');
        }

        failureReason = 'invalid_response';
        const responseData = await response.json();
        const returnedItems = Array.isArray(responseData?.items) ? responseData.items : [];
        const returnedVariantIds = returnedItems.map((item) => (
          String(item?.variant_id ?? item?.id ?? '')
        ));

        const bothItemsReturned = (
          requestedVariantIds.length === 2 &&
          returnedItems.length === 2 &&
          requestedVariantIds.every((variantId) => returnedVariantIds.includes(variantId))
        );

        if (!bothItemsReturned) {
          failureReason = 'item_mismatch';
          throw new Error('The full look could not be confirmed.');
        }

        publishCompleteLook(
          'hotheads_gallery:complete_look_added',
          {
            ...requestEventData,
            returned_item_count: returnedItems.length
          }
        );
      } catch (error) {
        publishCompleteLook(
          'hotheads_gallery:complete_look_add_failed',
          {
            ...requestEventData,
            failure_reason: failureReason,
            http_status: httpStatus
          }
        );

        cartReviewRequired = true;
        return;
      } finally {
        addInFlight = false;
        selects.forEach((select) => { select.disabled = false; });
        section.setAttribute('aria-busy', 'false');
        if (cartReviewRequired) syncCompleteLook();
      }

      window.location.assign(`${root}cart`);
    });

    syncCompleteLook();
  });

  if (document.querySelector('[data-mobile-purchase-bar]')) {
    document.body.classList.add('has-mobile-purchase-bar');
  }
})();

